import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  assignRole,
  getAccessPreview,
  listCatalogRoles,
  listProducts,
  listRoleAssignments,
  revokeRole,
} from './api/accessApi'
import {
  getUser,
  listUsers,
  registerUser,
  removeUserMembership,
  setUserActive,
  updateUser,
  type ListUsersParams,
} from './api/api'
import {
  accessPreviewKey,
  catalogRolesKey,
  productsListKey,
  roleAssignmentsKey,
  userDetailKey,
  usersKeys,
  usersListKey,
} from './keyFactory'
import type { UserInput } from './types'

// Defaults the list to active users so a deactivated user never shows up
// mixed in unless the caller explicitly asks for the deactivated view (see
// UsersList's "show deactivated" toggle, which passes `active: false`).
export function useUsers(params?: ListUsersParams) {
  const merged: ListUsersParams = { active: true, ...params }
  return useQuery({ queryKey: usersListKey(merged), queryFn: () => listUsers(merged) })
}

export function useUser(id: string) {
  return useQuery({ queryKey: userDetailKey(id), queryFn: () => getUser(id), enabled: !!id })
}

export function useRegisterUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UserInput) => registerUser(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersKeys.all() }),
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input, position }: { id: string; input: UserInput; position: string }) =>
      updateUser(id, input, position),
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all() })
      queryClient.invalidateQueries({ queryKey: userDetailKey(user.id) })
    },
  })
}

// Deactivating a user drops it out of the default (active) list — invalidate
// both the list and this user's own detail query so the edit screen (which
// stays mounted to show the confirmation result) reflects the new status too.
export function useSetUserActive() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => setUserActive(id, active),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all() })
      queryClient.invalidateQueries({ queryKey: userDetailKey(id) })
    },
  })
}

// Removing membership tombstones the user server-side, so — like
// deactivation — it drops out of the default list; unlike deactivation, it
// drops out of EVERY list state (the show-deactivated view too), so the same
// invalidation pair is enough.
export function useRemoveUserMembership() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => removeUserMembership(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all() })
      queryClient.invalidateQueries({ queryKey: userDetailKey(id) })
    },
  })
}

// Access tab hooks — assignment-led catalog roles per product.

export function useCatalogProducts() {
  return useQuery({ queryKey: productsListKey(), queryFn: listProducts })
}

export function useCatalogRoles(product: string) {
  return useQuery({
    queryKey: catalogRolesKey(product),
    queryFn: () => listCatalogRoles(product),
    enabled: !!product,
  })
}

export function useRoleAssignments(userId: string) {
  return useQuery({
    queryKey: roleAssignmentsKey(userId),
    queryFn: () => listRoleAssignments(userId),
    enabled: !!userId,
  })
}

export function useAccessPreview(userId: string, product: string) {
  return useQuery({
    queryKey: accessPreviewKey(userId, product),
    queryFn: () => getAccessPreview(userId, product),
    enabled: !!userId && !!product,
  })
}

// Assign/revoke both invalidate the assignments list AND the effective-access
// preview for the affected product — the preview must reflect the grant
// immediately, not just the assignment row.
export function useAssignRole(userId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ product, roleId }: { product: string; roleId: string }) => assignRole(userId, product, roleId),
    onSuccess: (_data, { product }) => {
      queryClient.invalidateQueries({ queryKey: roleAssignmentsKey(userId) })
      queryClient.invalidateQueries({ queryKey: accessPreviewKey(userId, product) })
    },
  })
}

export function useRevokeRole(userId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ product, roleId }: { product: string; roleId: string }) => revokeRole(userId, product, roleId),
    onSuccess: (_data, { product }) => {
      queryClient.invalidateQueries({ queryKey: roleAssignmentsKey(userId) })
      queryClient.invalidateQueries({ queryKey: accessPreviewKey(userId, product) })
    },
  })
}
